// components/dialogs/UnitSystemDialog.jsx
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import { useToast } from "../ui/use-toast";
import { supabase } from "../../lib/supabase";

export const UnitSystemDialog = ({ userPreferences, onUpdate }) => {
  const { toast } = useToast();
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [selectedUnit, setSelectedUnit] = React.useState(
    userPreferences?.unit || 'imperial'
  );

  const handleSave = async () => {
    if (selectedUnit === userPreferences?.unit) {
      setOpen(false);
      return;
    }

    setSaving(true);
    try {
      // Convert measurements based on unit change
      let newWeight = userPreferences.weight;
      let newHeight = { ...userPreferences.height };

      if (selectedUnit === 'metric' && userPreferences.unit === 'imperial') {
        // Convert to metric
        newWeight = Math.round(userPreferences.weight * 0.453592);
        newHeight = { 
          cm: Math.round(userPreferences.height_feet * 30.48 + userPreferences.height_inches * 2.54),
          feet: userPreferences.height_feet,
          inches: userPreferences.height_inches
        };
      } else if (selectedUnit === 'imperial' && userPreferences.unit === 'metric') {
        // Convert to imperial
        newWeight = Math.round(userPreferences.weight / 0.453592);
        const totalInches = Math.round(userPreferences.height_cm / 2.54);
        newHeight = {
          feet: Math.floor(totalInches / 12),
          inches: totalInches % 12,
          cm: userPreferences.height_cm
        };
      }

      const { error } = await supabase
        .from('user_preferences')
        .upsert({
          id: userPreferences.id,
          unit: selectedUnit,
          weight: newWeight,
          height_feet: newHeight.feet,
          height_inches: newHeight.inches,
          height_cm: newHeight.cm,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      toast({
        title: "Settings updated",
        description: "Your unit system has been updated successfully.",
      });

      // Call the onUpdate callback with the new preferences
      onUpdate({
        ...userPreferences,
        unit: selectedUnit,
        weight: newWeight,
        height_feet: newHeight.feet,
        height_inches: newHeight.inches,
        height_cm: newHeight.cm
      });

      setOpen(false);
    } catch (error) {
      console.error('Error updating unit system:', error);
      toast({
        title: "Error",
        description: "Failed to update unit system. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          {userPreferences?.unit === 'imperial' ? 'Imperial (US)' : 'Metric'}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Unit System</DialogTitle>
          <DialogDescription>
            Choose your preferred measurement system. Your measurements will be automatically converted.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <RadioGroup
            value={selectedUnit}
            onValueChange={setSelectedUnit}
            className="grid gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="imperial" id="imperial" />
              <Label htmlFor="imperial">Imperial (US)</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="metric" id="metric" />
              <Label htmlFor="metric">Metric</Label>
            </div>
          </RadioGroup>
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};